import { parsePhoneNumber } from 'awesome-phonenumber'
import { phoneCodeMapping, type RegionCode } from '../all-countries'
import { type CountryAndDialCode } from '../types/CountryAndDialCode'

function emptyNumber(number: string | undefined | null) {
  if (number === '' || number === undefined || typeof number === 'undefined') {
    return true
  }

  return false
}

function GetAllCountryNumbers(): CountryAndDialCode[] {
  const result: CountryAndDialCode[] = []
  phoneCodeMapping.forEach((country) => {
    const item = {
      CountryName: String(country[0]),
      CountryCode: String(country[1]).toUpperCase(),
      DialCode: String(country[2]),
      AreaCode: undefined
    }
    result.push(item)
  })
  return result
}

function getCountryAndDialCodeFromPhoneNumber(
  modelValue: string | undefined,
  countries: CountryAndDialCode[] = GetAllCountryNumbers()
) {
  if (!modelValue || emptyNumber(modelValue)) {
    return undefined
  }

  const validator = parsePhoneNumber(modelValue)
  if (validator.valid) {
    return (
      countries.find((i) => i.DialCode === validator.countryCode.toString()) ??
      undefined
    )
  }
  // If the str-value has +xx and a whitespace like ex. "+47 "
  if (modelValue.startsWith('+') && modelValue.includes(' ')) {
    const possibleCountryCode = modelValue.split(' ')[0].replace('+', '').trim()
    return (
      countries.find((i) => i.DialCode === possibleCountryCode) ?? undefined
    )
  }
  // Try matching a known dial code at the start (e.g. "+479065327" -> 47)
  const withoutPlus = modelValue.slice(1).trim()
  if (withoutPlus.length > 0) {
    const sorted = [...countries].sort(
      (a, b) => b.DialCode.length - a.DialCode.length
    )
    const match = sorted.find(
      (c) =>
        withoutPlus === c.DialCode ||
        withoutPlus.startsWith(c.DialCode)
    )
    if (match) return match
  }
  return undefined
}

function getPhoneNumberWithoutDailCode(
  text: string | undefined,
  selectedCountry: CountryAndDialCode | undefined
) {
  const resultText = text
  if (!resultText || emptyNumber(resultText)) {
    return undefined
  }
  if (resultText.startsWith('+') && selectedCountry) {
    const validator = parsePhoneNumber(resultText)
    if (validator.valid) {
      return validator.number.national
    }
    // Remove the + and dial code from the start
    const prefix = `+${selectedCountry.DialCode}`
    if (resultText.startsWith(prefix)) {
      return resultText.slice(prefix.length).trim()
    }
  }
  return resultText
}

function getCountryAndDialCodeFromRegionCode(
  regionCode: RegionCode | (string & {}) | undefined
) {
  if (!regionCode) {
    return undefined
  }
  return (
    GetAllCountryNumbers().find(
      (i) => i.CountryCode.toUpperCase() === regionCode.toUpperCase()
    ) ?? undefined
  )
}

function getCountryAndDialCodeFromDialCode(dialCode: number) {
  if (!Number.isNaN(dialCode)) {
    const res =
      GetAllCountryNumbers().find((i) => +i.DialCode === +dialCode) ?? undefined
    return res
  }
  return undefined
}

function valueIsDialCodeOnly(value: string) {
  const validator = parsePhoneNumber(value)
  if (value.startsWith('+') && !validator.valid && value.length <= 4) {
    if (validator.regionCode) {
      return (
        getCountryAndDialCodeFromRegionCode(
          validator.regionCode as RegionCode
        ) !== undefined
      )
    }
    return (
      getCountryAndDialCodeFromDialCode(+value.replace('+', '')) !== undefined
    )
  }
  return false
}

const GatPhoneEditFunctions = {
  getPhoneNumberWithoutDailCode,
  getCountryAndDialCodeFromPhoneNumber,
  getCountryAndDialCodeFromRegionCode,
  valueIsDialCodeOnly,
  emptyNumber,
  GetAllCountryNumbers
}

export default GatPhoneEditFunctions
